import { cva, type VariantProps } from 'class-variance-authority';
import { uiMarker, uiMarkerInternal, uiMarkerLayout } from './shared';

/**
 * LIST BESIDE ITS RECORD
 *
 * The inspections and trucks screens: a 400-wide list column on the left and
 * the record picked from it on the right, stacked below `lg`. Unlike
 * MasterDetail the list never narrows or slides away — the column is the
 * grouped list, flush, with every band and row drawing its own inset.
 *
 * `scroll` is for a screen that fits the shell's height (`Page fill="lg"`):
 * from `lg` up each column scrolls on its own and the heading, the tabs and
 * the list stay where they are. Without it the page scrolls as one.
 */
export const listDetailLayoutRecipe = cva(
  [uiMarker, uiMarkerLayout, 'flex w-full flex-col items-start lg:flex-row'],
  {
    variants: {
      scroll: { true: 'lg:min-h-0 lg:flex-1', false: '' },
    },
    defaultVariants: { scroll: false },
  }
);

export const listDetailLayoutListRecipe = cva(
  [uiMarkerInternal, 'flex w-full flex-col lg:w-[400px] lg:shrink-0'],
  {
    variants: {
      scroll: { true: 'lg:h-full lg:min-h-0 lg:overflow-y-auto', false: '' },
      /**
       * Takes back the 16px the page already insets by, as ListColumn's
       * `bleed` does: the toolbar, the bands and the rows each carry that
       * inset themselves, so inside a padded page they sat 16px further in
       * than the title above them. Stacked, the column reaches both edges;
       * beside the record, only the left one moves.
       */
      bleed: {
        true: '-mx-4 w-[calc(100%+2rem)] lg:mx-0 lg:-ml-4 lg:w-[400px]',
        false: '',
      },
      /** The record has the screen. Only from `lg`: below it they stack. */
      expanded: { true: 'lg:hidden', false: '' },
    },
    defaultVariants: { scroll: false, bleed: false, expanded: false },
  }
);

/** The list's search: it stays put while the rows scroll under it. */
export const listDetailLayoutSearchRecipe = cva([
  uiMarkerInternal,
  'sticky top-0 z-10 bg-background pb-3',
]);

/**
 * The record, level with the search above the list, and 16 clear of the
 * list's scrollbar, which it butted against. With the list gone it has no
 * neighbour to stand off from.
 */
export const listDetailLayoutDetailRecipe = cva(
  [uiMarkerInternal, 'w-full min-w-0 lg:flex-1'],
  {
    variants: {
      scroll: {
        true: 'lg:h-full lg:min-h-0 lg:overflow-y-auto lg:overflow-x-hidden',
        false: '',
      },
      expanded: { true: '', false: 'lg:pl-4' },
    },
    defaultVariants: { scroll: false, expanded: false },
  }
);

/** The line above a record the list does not show. */
export const listScreenNoticeRecipe = cva([uiMarkerInternal, 'pb-2']);

export type ListDetailLayoutRecipeProps = VariantProps<
  typeof listDetailLayoutRecipe
>;
